import { FC, useState } from 'react';
import {
  EntourageEffect,
  Superstar,
} from '@wwe-portal/entities/superstar.type';
import { SuperstarLink } from '@wwe-portal/web/components/common/link/superstar-link';
import { RosterAvatar } from './roster-avatar';
import { FillImage } from '@wwe-portal/ui/components/image';
import { RosterViewMoreButton } from './roster-view-more-button';
import Image from 'next/image';
import { cloneArrayWithId } from '@wwe-portal/utils/array';

export interface EntourageListItemProps {
  data: {
    superstarInfo: Pick<
      Superstar,
      'groupId' | 'name' | 'avatarSS' | 'tier' | 'entourageType'
    >;
    effects: Pick<EntourageEffect, 'text' | 'talentScore' | 'relatedEffect'>[];
  };
}

export const EntourageListItem: FC<EntourageListItemProps> = ({ data }) => {
  const { effects } = data;
  const [isMore, setIsMore] = useState(false);
  const mainItem = effects.find((item) => item.relatedEffect);

  const groupedByTalentScore = effects.reduce<
    Record<number, { text: string }[]>
  >((acc, item) => {
    const { talentScore } = item;
    acc[talentScore] = acc[talentScore] ?? [];
    acc[talentScore].push({ text: item.text });
    return acc;
  }, {});

  const groupedArray = Object.entries(groupedByTalentScore).map(
    ([talentScore, items]) => ({
      talentScore: Number(talentScore),
      items: cloneArrayWithId(items),
    })
  );

  return (
    <div className="drop-shadow-card relative flex flex-col">
      <div className="relative min-h-[96px] w-full">
        <div className="absolute inset-0 -z-10 h-[96px] bg-[url('assets/images/roster/entourage-search-item-bg.webp')] bg-cover bg-left bg-no-repeat" />
        <div className="mt-[42px]">
          {isMore ? (
            <div className="mb-2 ml-[85px] flex h-auto min-h-[52px] flex-col">
              {groupedArray.map((groupItem) => {
                return (
                  <div
                    className="flex flex-col bg-[#1B1E37] px-2 pt-5 italic"
                    key={groupItem.talentScore}
                  >
                    <div className="mb-3">
                      <PowerBadge value={groupItem.talentScore} />
                    </div>
                    <div>
                      {groupItem.items.map((item) => (
                        <div
                          key={item.id}
                          className="border-borderColor16 border-b p-2 text-xs first:border-t last:border-b-0"
                          style={{
                            background:
                              'linear-gradient(90deg, rgba(0, 0, 0, 0.22) 0%, rgba(15, 15, 15, 0) 100%)',
                          }}
                        >
                          + {item.text}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="ml-[85px] flex min-h-[52px] flex-col items-center justify-center px-2">
              <div className="drop-shadow-shade-strong line-clamp-2 w-full text-xs font-medium italic">
                {mainItem?.text}
              </div>
            </div>
          )}
        </div>
        <div className="absolute inset-0 bottom-3 top-2 h-full w-full">
          <div
            className="mt-2 flex h-6 w-full items-center justify-between pl-[94px] pr-2 text-xs font-semibold leading-none"
            style={{
              boxShadow: '0px 1px 4px 4px rgba(0, 0, 0, 0.3)',
              background:
                'linear-gradient(90deg, rgba(23, 69, 30, 0.69) 0%, rgba(31, 115, 44, 0.69) 80.29%, rgba(6, 57, 13, 0.69) 97.5%)',
            }}
          >
            <div>
              {isMore ? (
                <EntourageType
                  type={data.superstarInfo.entourageType ?? '???'}
                />
              ) : (
                <PowerBadge value={mainItem?.talentScore ?? 0} />
              )}
            </div>
            <RosterViewMoreButton
              onClick={() => {
                setIsMore(!isMore);
              }}
              isMore={!isMore}
            />
          </div>

          <div className="absolute inset-y-0 left-4 h-full w-[67px]">
            <SuperstarLink superstarId={data.superstarInfo.groupId}>
              <RosterAvatar
                src={''}
                imageElement={
                  <FillImage
                    src={data.superstarInfo.avatarSS ?? ''}
                    className="absolute inset-0 h-full w-full object-contain"
                    alt="move avatar"
                    fill
                    errorImage={<></>}
                  />
                }
                tier={data.superstarInfo.tier}
              />
            </SuperstarLink>
          </div>
        </div>
      </div>
    </div>
  );
};

const EntourageType: FC<{ type: string }> = ({ type }) => {
  return (
    <div className="relative">
      <div className="relative z-10 px-1 py-2 text-sm font-semibold uppercase text-white">
        {type}
      </div>
      <div className="absolute inset-0">
        <Image
          width={69}
          height={28}
          src="/assets/images/superstar/entourage-type-bg.webp"
          alt="entourage type"
          className="h-full w-full object-fill"
        />
      </div>
    </div>
  );
};

export const PowerBadge: FC<{ value: number }> = ({ value }) => {
  return (
    <div className="relative h-fit w-fit">
      <div className="absolute inset-0 -bottom-2 -top-1">
        <Image
          width={68}
          height={28}
          src="/assets/images/superstar/entourage-power-badge-bg.webp"
          alt="entourage power bg"
          className="h-full w-full object-fill"
        />
      </div>
      <span className="drop-shadow-text-2 w-auto px-2 text-sm font-semibold italic text-white">
        {value}
      </span>
    </div>
  );
};
